import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
const FALLBACK_UF = 39841.72;
const MINGA_UF = 150;
export function CalculatorSection() {
  const [ufValue, setUfValue] = useState(FALLBACK_UF);
  const [ufDate, setUfDate] = useState('');
  const [sliderUF, setSliderUF] = useState(10000);
  useEffect(() => {
    fetch('https://mindicador.cl/api/uf').then(res => res.json()).then(data => {
      if (data?.serie?.[0]) {
        const latest = data.serie[0];
        setUfValue(latest.valor);
        const date = new Date(latest.fecha);
        setUfDate(date.toLocaleDateString('es-CL', {
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        }));
      }
    }).catch(() => {
      // Keep fallback value on error
    });
  }, []);
  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSliderUF(parseInt(e.target.value, 10));
  };
  const MINGA_COST = MINGA_UF * ufValue;
  const numericPrice = sliderUF * ufValue;
  const traditionalCost = numericPrice * 0.02 * 1.19;
  const difference = traditionalCost - MINGA_COST;
  const pie = numericPrice * 0.2;
  const loan = numericPrice * 0.8;
  const r = 0.044 / 12;
  const n = 240;
  const dividendo = loan > 0 ? loan * (r * Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1)) : 0;
  const formatCLP = (val: number) => {
    return '$' + new Intl.NumberFormat('es-CL').format(Math.round(val));
  };
  const formatUF = (val: number) => {
    return new Intl.NumberFormat('es-CL').format(val) + ' UF';
  };
  return <section className="py-16 px-6 bg-white" data-id="element-6">
      <div className="max-w-2xl mx-auto" data-id="element-7">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-white rounded-3xl border border-gray-200 shadow-sm p-6 md:p-10" data-id="element-8">
          <div className="text-center mb-8" data-id="element-9">
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-gray-900 mb-2" data-id="element-10">
              ¿Cuánto estás pagando sin darte cuenta?
            </h2>
          </div>

          {/* 1. INPUT (SLIDER) */}
          <div className="mb-10" data-id="element-11">
            <div className="flex justify-between items-end mb-4" data-id="element-12">
              <label htmlFor="uf-slider" className="block text-sm font-medium text-gray-700" data-id="element-13">
                Precio de la propiedad
              </label>
              <div className="text-right" data-id="element-14">
                <span className="block text-xl font-bold text-brand-600" data-id="element-15">
                  {formatUF(sliderUF)}
                </span>
                <span className="block text-sm text-gray-500" data-id="element-16">
                  ≈ {formatCLP(numericPrice)}
                </span>
              </div>
            </div>

            <div className="relative w-full h-6 flex items-center" data-id="element-17">
              <input id="uf-slider" type="range" min="5000" max="50000" step="100" value={sliderUF} onChange={handleSliderChange} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2" style={{
              background: `linear-gradient(to right, #2563eb 0%, #2563eb ${(sliderUF - 5000) / 45000 * 100}%, #e5e7eb ${(sliderUF - 5000) / 45000 * 100}%, #e5e7eb 100%)`
            }} data-id="element-18" />
            </div>
            <p className="text-xs text-gray-400 mt-3 text-center" data-id="element-19">
              UF{ufDate ? ` al ${ufDate}` : ''}: $
              {new Intl.NumberFormat('es-CL', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            }).format(ufValue)}{' '}
              (Banco Central de Chile)
            </p>
          </div>

          {/* 2. COMPARACIÓN + AHORRO (MAIN) */}
          <div className="bg-gray-50 rounded-2xl p-6 md:p-8 border border-gray-100 mb-8" data-id="element-20">
            {/* Horizontal Comparison */}
            <div className="flex flex-row justify-between items-center mb-8 pb-8 border-b border-gray-200" data-id="element-21">
              <div className="text-left w-1/2 pr-4 border-r border-gray-200" data-id="element-22">
                <span className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1" data-id="element-23">
                  Con corredor
                </span>
                <span className="block text-xl md:text-2xl font-bold text-gray-900 mb-1" data-id="element-24">
                  {formatCLP(traditionalCost)}
                </span>
                <span className="block text-xs text-gray-500" data-id="element-25">2% + IVA</span>
              </div>

              <div className="text-right w-1/2 pl-4" data-id="element-26">
                <span className="block text-xs font-semibold text-brand-600 uppercase tracking-wider mb-1" data-id="element-27">
                  Con MINGA
                </span>
                <span className="block text-xl md:text-2xl font-bold text-brand-600 mb-1" data-id="element-28">
                  {formatCLP(MINGA_COST)}
                </span>
                <span className="block text-xs text-gray-500" data-id="element-29">
                  150 UF total (IVA incluido)
                </span>
              </div>
            </div>

            {/* CORE BLOCK: SAVINGS */}
            <div className="text-center" data-id="element-30">
              {difference > 0 ? <>
                  <p className="text-2xl md:text-4xl font-extrabold text-gray-900 tracking-tight mb-2 leading-tight" data-id="element-31">
                    Estás perdiendo{' '}
                    <span className="text-brand-600" data-id="element-32">
                      {formatCLP(difference)}
                    </span>{' '}
                    con el modelo tradicional
                  </p>
                  <p className="text-sm md:text-base text-gray-600 font-medium" data-id="element-33">
                    Es dinero que podrías usar en tu pie, remodelación o
                    simplemente no pagar de más.
                  </p>
                </> : <>
                  <p className="text-2xl md:text-4xl font-extrabold text-gray-900 tracking-tight mb-2 leading-tight" data-id="element-34">
                    Con MINGA pagas un{' '}
                    <span className="text-brand-600" data-id="element-35">precio justo</span>
                  </p>
                  <p className="text-sm md:text-base text-gray-600 font-medium" data-id="element-36">
                    Transparente y sin comisiones abusivas.
                  </p>
                </>}
            </div>
          </div>

          {/* 3. CONTEXTO FINANCIERO (SECONDARY) */}
          <div className="mt-6 bg-gray-50 rounded-xl border border-gray-100 p-5" data-id="element-37">
            <div className="flex flex-row items-center" data-id="element-38">
              <div className="flex-1 text-center" data-id="element-39">
                <span className="block text-xs text-gray-400 font-medium mb-1" data-id="element-40">
                  Pie estimado (20%)
                </span>
                <span className="block text-sm font-semibold text-gray-600" data-id="element-41">
                  {formatCLP(pie)}
                </span>
              </div>

              <div className="w-px h-10 bg-gray-200 mx-4 shrink-0" data-id="element-42" />

              <div className="flex-1 text-center" data-id="element-43">
                <span className="block text-xs text-gray-400 font-medium mb-1" data-id="element-44">
                  Dividendo estimado{' '}
                  <span className="font-normal" data-id="element-45">(20 años, 4,4%)</span>
                </span>
                <span className="block text-sm font-semibold text-gray-600" data-id="element-46">
                  {formatCLP(dividendo)} / mes
                </span>
              </div>
            </div>

            <p className="text-center text-[10px] text-gray-400 mt-4" data-id="element-47">
              Valores referenciales. El dividendo puede variar según condiciones
              del crédito.
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
}